import { Fragment, useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Line,
  LineChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, Truck, Undo2, Target, Building2, FileDown, ChevronDown, ChevronRight } from "lucide-react";

const REASON_LABELS = {
  defectuoso: "Defectuoso",
  error_pedido: "Error de pedido",
  sobre_stock: "Sobre stock",
  cambio_modelo: "Cambio de modelo",
  no_adaptacion: "No adaptación",
  otro: "Otro",
};
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Reports() {
  const { user, centers } = useAuth();
  const [centerFilter, setCenterFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [salesYear, setSalesYear] = useState(String(new Date().getFullYear()));
  const [salesBySupplier, setSalesBySupplier] = useState([]);
  const [expandedSup, setExpandedSup] = useState(null);
  const [xlsxLoading, setXlsxLoading] = useState(false);

  const exportSuppliersXlsx = async () => {
    setXlsxLoading(true);
    try {
      const params = {};
      if (salesYear !== "all") params.year = salesYear;
      const resp = await api.get("/stats/sales-by-supplier/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `ventas_por_proveedor_${salesYear === "all" ? "todo" : salesYear}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel de proveedores descargado");
    } catch {
      toast.error("No se pudo generar el Excel");
    } finally {
      setXlsxLoading(false);
    }
  };
  const [returnsYear, setReturnsYear] = useState(String(new Date().getFullYear()));
  const [returnsData, setReturnsData] = useState(null);
  const [goalHistory, setGoalHistory] = useState(null);
  const [goalScope, setGoalScope] = useState("all");
  const [centerComp, setCenterComp] = useState(null);
  const [pdfMonth, setPdfMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [pdfLoading, setPdfLoading] = useState(false);

  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const raw = d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
    return { value, label: raw.charAt(0).toUpperCase() + raw.slice(1) };
  });

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const resp = await api.get("/reports/monthly-pdf", { params: { month: pdfMonth }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe_${pdfMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Informe PDF descargado");
    } catch {
      toast.error("No se pudo generar el informe PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    api.get("/stats/sales-by-center", { params: { months: 12 } })
      .then((r) => setCenterComp(r.data))
      .catch(() => setCenterComp(null));
  }, []);

  useEffect(() => {
    const params = { months: 12 };
    if (goalScope !== "all") params.center_id = goalScope;
    api.get("/stats/goal-history", { params })
      .then((r) => setGoalHistory(r.data))
      .catch(() => setGoalHistory(null));
  }, [goalScope]);

  useEffect(() => {
    const params = {};
    if (salesYear !== "all") params.year = salesYear;
    api.get("/stats/sales-by-supplier", { params })
      .then((r) => setSalesBySupplier(r.data?.rows || []))
      .catch(() => setSalesBySupplier([]));
  }, [salesYear]);

  useEffect(() => {
    const params = {};
    if (returnsYear !== "all") params.year = returnsYear;
    api.get("/stats/returns-by-brand", { params })
      .then((r) => setReturnsData(r.data))
      .catch(() => setReturnsData(null));
  }, [returnsYear]);

  useEffect(() => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    api.get("/reports/summary", { params }).then((r) => setSummary(r.data));
  }, [centerFilter, user]);

  if (!summary) return <div className="text-[#6E7772]">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Informes
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Ingresos y gastos
          </h1>
        </div>
        {["admin", "gerente"].includes(user?.role) && (
          <div className="flex items-end gap-2 flex-wrap">
            <div className="w-40">
              <Select value={pdfMonth} onValueChange={setPdfMonth}>
                <SelectTrigger className="bg-white" data-testid="pdf-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {monthOptions.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={downloadPdf}
              disabled={pdfLoading}
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
              data-testid="download-monthly-pdf"
            >
              <FileDown className="h-4 w-4 mr-2" />
              {pdfLoading ? "Generando…" : "Informe PDF"}
            </Button>
          </div>
        )}
        {["admin", "center_admin"].includes(user?.role) && (
          <div className="w-56">
            <Select value={centerFilter} onValueChange={setCenterFilter}>
              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-[#E5E3DB] shadow-none bg-[#D1E0D7]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772]">
                  Ingresos totales
                </div>
                <div className="font-heading text-3xl text-[#2A2F2C] mt-2">
                  {euro(summary.total_income)}
                </div>
              </div>
              <TrendingUp className="h-6 w-6 text-[#4A5D4E]" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E3DB] shadow-none bg-[#F3E5D8]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772]">
                  Gastos totales
                </div>
                <div className="font-heading text-3xl text-[#2A2F2C] mt-2">
                  {euro(summary.total_expense)}
                </div>
              </div>
              <TrendingDown className="h-6 w-6 text-[#C07F67]" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>
        <Card className={`border-[#E5E3DB] shadow-none ${summary.balance >= 0 ? "bg-white" : "bg-[#E8D1D1]"}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772]">
                  Balance
                </div>
                <div className={`font-heading text-3xl mt-2 ${summary.balance >= 0 ? "text-[#4A5D4E]" : "text-[#C07F67]"}`} data-testid="balance-value">
                  {euro(summary.balance)}
                </div>
              </div>
              <Wallet className="h-6 w-6 text-[#4A5D4E]" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg">
            Evolución mensual
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 360 }}>
            <ResponsiveContainer>
              <LineChart data={summary.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                <XAxis dataKey="month" stroke="#6E7772" fontSize={12} />
                <YAxis stroke="#6E7772" fontSize={12} />
                <Tooltip
                  formatter={(v) => euro(v)}
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #E5E3DB",
                    borderRadius: 8,
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="income" name="Ingresos" stroke="#4A5D4E" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="expense" name="Gastos" stroke="#C07F67" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="sales-by-supplier-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
              <Truck className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} /> Ventas por proveedor
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={exportSuppliersXlsx}
                disabled={xlsxLoading || salesBySupplier.length === 0}
                data-testid="export-suppliers-xlsx"
              >
                <FileDown className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                {xlsxLoading ? "Generando…" : "Excel"}
              </Button>
              <div className="w-36">
                <Select value={salesYear} onValueChange={setSalesYear}>
                  <SelectTrigger className="bg-white h-8" data-testid="sales-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todo</SelectItem>
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {salesBySupplier.length === 0 ? (
            <p className="text-sm text-[#6E7772]">Sin ventas registradas en el periodo.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E3DB]">
                  <TableHead>Proveedor</TableHead>
                  <TableHead className="text-right">Unidades vendidas</TableHead>
                  <TableHead className="text-right">Importe (IVA incl.)</TableHead>
                  <TableHead className="text-right">Margen (sin IVA)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesBySupplier.map((r, i) => (
                  <Fragment key={i}>
                    <TableRow
                      className="border-[#E5E3DB] cursor-pointer hover:bg-[#FAF9F6]"
                      data-testid={`supplier-sales-row-${i}`}
                      onClick={() => setExpandedSup(expandedSup === i ? null : i)}
                    >
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {expandedSup === i ? (
                            <ChevronDown className="h-3.5 w-3.5 text-[#6E7772]" strokeWidth={2} />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-[#6E7772]" strokeWidth={2} />
                          )}
                          {r.supplier_name}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{r.units}</TableCell>
                      <TableCell className="text-right">{euro(r.amount)}</TableCell>
                      <TableCell className="text-right">
                        {r.margin != null ? (
                          <span className={r.margin >= 0 ? "text-[#4A5D4E] font-medium" : "text-red-600 font-medium"}>
                            {euro(r.margin)}{r.margin_pct != null ? ` (${r.margin_pct}%)` : ""}
                          </span>
                        ) : (
                          <span className="text-[#6E7772]">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedSup === i && (
                      <TableRow className="border-[#E5E3DB] bg-[#FAF9F6]" data-testid={`supplier-products-detail-${i}`}>
                        <TableCell colSpan={4} className="py-2">
                          <div className="pl-6">
                            <p className="text-[11px] uppercase tracking-wider text-[#6E7772] mb-1.5">
                              Productos concretos vendidos de este proveedor
                            </p>
                            {(r.products || []).length === 0 ? (
                              <p className="text-xs text-[#6E7772]">Sin detalle de productos.</p>
                            ) : (
                              <div className="max-w-4xl">
                                <div className="grid grid-cols-[1fr_55px_120px_120px_150px] gap-x-3 text-[10px] uppercase tracking-wider text-[#6E7772] border-b border-[#E5E3DB] pb-1">
                                  <span>Producto</span>
                                  <span className="text-right">Uds.</span>
                                  <span className="text-right">Venta (sin IVA)</span>
                                  <span className="text-right">Coste compra</span>
                                  <span className="text-right">Margen</span>
                                </div>
                                {r.products.map((p, j) => (
                                  <div
                                    key={j}
                                    className="grid grid-cols-[1fr_55px_120px_120px_150px] gap-x-3 text-sm py-0.5"
                                    data-testid={`supplier-${i}-product-${j}`}
                                  >
                                    <span>{p.name}</span>
                                    <span className="text-right tabular-nums">{p.units}</span>
                                    <span className="text-right tabular-nums">{euro(p.sale_base)}</span>
                                    <span className="text-right tabular-nums">{p.cost_total != null ? euro(p.cost_total) : "—"}</span>
                                    <span className={`text-right tabular-nums ${p.margin == null ? "text-[#6E7772]" : p.margin >= 0 ? "text-[#4A5D4E] font-medium" : "text-red-600 font-medium"}`}>
                                      {p.margin != null ? `${euro(p.margin)}${p.margin_pct != null ? ` (${p.margin_pct}%)` : ""}` : "—"}
                                    </span>
                                  </div>
                                ))}
                                <p className="text-[11px] text-[#6E7772] mt-1.5">
                                  El coste sale del campo «Coste de compra» del artículo en Stock o, si no está relleno, del último albarán con esa marca y modelo.
                                </p>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
          {salesBySupplier.length > 0 && (
            <p className="text-[11px] text-[#6E7772] mt-2">
              Haz clic en un proveedor para ver el detalle de unidades por producto concreto.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="returns-by-brand-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-[#C07F67]" strokeWidth={1.5} /> Devoluciones por marca
            </CardTitle>
            <div className="w-36">
              <Select value={returnsYear} onValueChange={setReturnsYear}>
                <SelectTrigger className="bg-white h-8" data-testid="returns-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo</SelectItem>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!returnsData || returnsData.rows.length === 0 ? (
            <p className="text-sm text-[#6E7772]">Sin devoluciones registradas en el periodo.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-4 mb-4 text-sm text-[#4B5563]" data-testid="returns-totals">
                <span><strong>{returnsData.total.units_returned}</strong> unidades devueltas</span>
                <span><strong>{euro(returnsData.total.amount_refunded)}</strong> reembolsados</span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E5E3DB]">
                    <TableHead>Marca</TableHead>
                    <TableHead className="text-right">Uds. devueltas</TableHead>
                    <TableHead className="text-right">Uds. vendidas</TableHead>
                    <TableHead className="text-right">% devolución</TableHead>
                    <TableHead className="text-right">Reembolsado</TableHead>
                    <TableHead>Motivo principal</TableHead>
                    <TableHead className="text-right">Prov. / Pac.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {returnsData.rows.map((r, i) => (
                    <TableRow key={i} className="border-[#E5E3DB]" data-testid={`returns-brand-row-${i}`}>
                      <TableCell className="font-medium">{r.brand}</TableCell>
                      <TableCell className="text-right">{r.units_returned}</TableCell>
                      <TableCell className="text-right">{r.sold_units || "—"}</TableCell>
                      <TableCell className="text-right">
                        {r.return_rate == null ? "—" : (
                          <span className={r.return_rate >= 20 ? "text-[#C0392B] font-semibold" : ""}>{r.return_rate}%</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{euro(r.amount_refunded)}</TableCell>
                      <TableCell className="text-sm">{REASON_LABELS[r.top_reason] || r.top_reason || "—"}</TableCell>
                      <TableCell className="text-right text-xs text-[#6E7772]">{r.supplier_returns} / {r.patient_returns}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-[11px] text-[#9CA3AF] mt-2">
                "Uds. vendidas" = audífonos adaptados activos de la marca en el periodo. "Prov. / Pac." = devoluciones al proveedor / devoluciones de pacientes.
              </p>
            </>
          )}
        </CardContent>
      </Card>
      <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="center-comparison-card">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} /> Comparativa de ventas por centro (últimos 12 meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            if (!centerComp || (centerComp.centers || []).length === 0) {
              return <p className="text-sm text-[#6E7772]">Sin datos de centros.</p>;
            }
            const COLORS = ["#4A5D4E", "#C07F67", "#7C5A2A", "#C9A961"];
            const shortName = (n) => (n || "").split(" - ").pop() || n;
            const fmtShort = (mk) => {
              const d = new Date(mk + "-01T12:00:00");
              const n = d.toLocaleDateString("es-ES", { month: "short" });
              return `${n.charAt(0).toUpperCase()}${n.slice(1).replace(".", "")} ${String(d.getFullYear()).slice(2)}`;
            };
            const chartData = centerComp.rows.map((r) => {
              const o = { month: fmtShort(r.month) };
              centerComp.centers.forEach((c) => {
                o[shortName(c.name)] = r.centers[c.id]?.total || 0;
              });
              return o;
            });
            return (
              <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6E7772" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#6E7772" }} />
                      <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderColor: "#E5E3DB", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {centerComp.centers.map((c, i) => (
                        <Bar key={c.id} dataKey={shortName(c.name)} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-4 mt-3" data-testid="center-comparison-totals">
                  {centerComp.centers.map((c, i) => {
                    const t = centerComp.totals?.[c.id] || { total: 0, count: 0 };
                    return (
                      <div key={c.id} className="rounded border border-[#E5E3DB] px-3 py-2 text-sm">
                        <span className="inline-block h-2.5 w-2.5 rounded-sm mr-2" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="font-medium text-[#2A2F2C]">{shortName(c.name)}</span>
                        <span className="text-[#6E7772]"> · {euro(t.total)} · {t.count} factura{t.count === 1 ? "" : "s"}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="goal-history-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} /> Objetivos mensuales de ventas
            </CardTitle>
            <div className="w-52">
              <Select value={goalScope} onValueChange={setGoalScope}>
                <SelectTrigger className="bg-white h-8" data-testid="goal-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Global (todos los centros)</SelectItem>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const rows = (goalHistory?.rows || []).filter((r) => r.goal != null);
            if (rows.length === 0) {
              return (
                <p className="text-sm text-[#6E7772]" data-testid="goal-history-empty">
                  Aún no hay objetivos registrados. Fija el objetivo del mes desde el Panel y aquí verás el histórico de cumplimiento.
                </p>
              );
            }
            const fmtMonth = (mk) => {
              const n = new Date(mk + "-01T12:00:00").toLocaleDateString("es-ES", { month: "long", year: "numeric" });
              return n.charAt(0).toUpperCase() + n.slice(1);
            };
            return (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E5E3DB]">
                    <TableHead>Mes</TableHead>
                    <TableHead className="text-right">Objetivo</TableHead>
                    <TableHead className="text-right">Ventas</TableHead>
                    <TableHead className="text-right">% conseguido</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.month} className="border-[#E5E3DB]" data-testid={`goal-history-row-${r.month}`}>
                      <TableCell className="font-medium">{fmtMonth(r.month)}{r.in_progress ? " (en curso)" : ""}</TableCell>
                      <TableCell className="text-right">{euro(r.goal)}</TableCell>
                      <TableCell className="text-right">{euro(r.sales)}</TableCell>
                      <TableCell className="text-right">
                        <span className={r.achieved ? "text-[#4A5D4E] font-semibold" : ""}>{r.pct != null ? `${r.pct}%` : "—"}</span>
                      </TableCell>
                      <TableCell>
                        {r.achieved ? (
                          <span className="inline-flex items-center rounded-full bg-[#D1E0D7] text-[#2A2F2C] px-2 py-0.5 text-xs font-medium">Conseguido</span>
                        ) : r.in_progress ? (
                          <span className="inline-flex items-center rounded-full bg-[#FBF3E4] text-[#7C5A2A] px-2 py-0.5 text-xs font-medium">En curso</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-[#E8D1D1] text-[#8A2E2E] px-2 py-0.5 text-xs font-medium">No conseguido</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
